<script>
/**
 * Índice "Artefatos desta conversa" — só em `/home-ia`. A linha abre o aside
 * para a prévia E para o cartão inline (os dois registram `parteReal`); sem
 * `parteReal` — cartão de escrita, ou kill-switch desligado — o
 * `messages.vue` rola até o cartão. Vive no canto superior direito do trilho
 * (a casca sticky/absoluta é do `messages.vue`) em UMA de três visões
 * trocadas no MESMO lugar (a doc completa é o §5.1 do CLAUDE.md do módulo):
 *
 * - `compacta`: a régua de tiles de 40px (`LARGURA_DA_REGUA`) do irmão
 *   `chat-indice-artefatos-compacta.vue` — o estado MÍNIMO do índice. É o
 *   que a tela mostra quando não cabe menu ao lado do trilho E quando o
 *   usuário minimizou (preferência de régua em `localStorage`,
 *   `preferencia-de-regua.js`; expandir apaga). ★ A régua é o piso desde
 *   14/09/2026 (decisão do usuário: já é pequena e continua um índice
 *   clicável) — não há estado menor que ela; não criar.
 * - `condensada`: o MENU (`VISAO_ABERTA_PADRAO`): cabeçalho curto + contagem
 *   + minimizar (→ régua), uma linha por artefato só com ícone do módulo +
 *   título truncado, hairline + `--shadow-sm` sobre `--white` (o "sem borda"
 *   sumia no trilho branco). O ícone anima no hover pelo MESMO partial da
 *   navbar/card (`_nav-icon-tells.sass`, hover por JS em `linhaEmHover`).
 * - `completa` (~360px, irmão `chat-indice-artefatos-completa.vue`): viva,
 *   fora da UI (só `alternarDensidade()`, sem botão) — ver o irmão.
 *
 * ★ A faixa ao lado do trilho (`larguraMaxima`, medida pelo `messages.vue`;
 * nulo = sem medida, o menu segue o conteúdo 180-240): ≥ 128
 * (`PISO_DO_CARTAO_CONDENSADO`) o menu CABE → largura FIXA
 * `clamp(128, faixa, 240)`; < 128 (inclusive 0: trilho ocupando o container)
 * NÃO cabe. `visaoEfetiva`: preferência de régua → régua; cabe → menu; não
 * cabe → régua, salvo se MAXIMIZADO pelo chevron da régua: aí a condensada
 * SOBREPOSTA ao chat (`chat-indice-artefatos-maximizado-mixin.js`: estado
 * local, "Recolher" no cabeçalho, ESC e clique fora; a faixa cabendo desfaz
 * sozinha). Recolher/abrir a lateral troca menu ↔ régua sozinho — exceto
 * com a preferência, que é escolha.
 *
 * A troca entre visões é a transição `iv2-indice-artefatos` (no `<style>`):
 * SEM `mode` — coexistem por 200ms, quem sai vira absoluto no canto e some
 * em fade; nunca há um quadro sem nada no canto (o porquê está na regra).
 *
 * Componente BURRO, no molde de `chat-indice-mensagens.vue`: recebe
 * `artefatos` (getter `artefatosDaConversa`) e emite `abrir`
 * (`{identidade, parteReal}`) ou `fechar` — o MESMO toggle do card do thread
 * (na completa quem decide é o card mini; na condensada/régua, a linha).
 * Quem despacha é o `messages.vue`. Escolher uma linha NÃO minimiza; não
 * rola até a mensagem (decisão do usuário, 11/09/2026).
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { INDICE_ARTEFATOS } from '@modules/aawz-intelligence-v2/constants/TextChat'
import ChatIndiceArtefatosCompleta, {
  LARGURA_COMPLETA,
} from './chat-indice-artefatos-completa.vue'
import ChatIndiceArtefatosCompacta from './chat-indice-artefatos-compacta.vue'
import {
  ICONE_DE_RECOLHER,
  LARGURA_MAXIMA_DO_CARTAO,
  RESPIRO_DO_TOPO,
  TAMANHO_DO_ICONE_DA_LINHA,
  TAMANHO_DO_ICONE_DO_TOPO,
  VISAO_ABERTA_PADRAO,
  VISOES_DO_INDICE,
  alturaMaximaDaListaDoMenu,
  larguraDoCartaoCondensado,
} from './chat-indice-artefatos.constantes'
import { gravarPrefereRegua, lerPrefereRegua } from './preferencia-de-regua'
import { linhasDoIndice } from './linhas-do-indice'
import maximizadoMixin from './chat-indice-artefatos-maximizado-mixin'

export * from './chat-indice-artefatos.constantes' // consumidores e testes importam daqui

const STORE_MODULE = 'aawz-intelligence-v2'

export default {
  name: 'IntelligenceV2ChatIndiceArtefatos',
  components: { ChatIndiceArtefatosCompleta, ChatIndiceArtefatosCompacta },
  mixins: [maximizadoMixin],
  props: {
    // O getter `artefatosDaConversa` (`store/artefato.js`); shape em `linhas-do-indice.js`.
    artefatos: {
      type: Array,
      default: () => [],
    },
    // Faixa livre (px) ao lado do trilho (`messages.vue`); ver o cabeçalho.
    larguraMaxima: {
      type: Number,
      default: null,
    },
    // O namespace de quem monta (na Home IA, a CONVERSA) — ver a prop homônima em `artefato-preview-card.vue`.
    storeModule: { type: String, default: STORE_MODULE },
  },
  data() {
    return {
      ICONE_DE_RECOLHER,
      TAMANHO_DO_ICONE_DO_TOPO,
      TAMANHO_DO_ICONE_DA_LINHA,
      titulo: INDICE_ARTEFATOS.TITULO,
      tituloCurto: INDICE_ARTEFATOS.TITULO_CURTO,
      rotuloMinimizar: INDICE_ARTEFATOS.MINIMIZAR,
      // A densidade do MENU (condensada/completa); a tela mostra `visaoEfetiva`.
      visao: VISAO_ABERTA_PADRAO,
      // Quem minimizou quer a régua (persistida); expandir apaga.
      prefereRegua: lerPrefereRegua(),
      // Linha sob o mouse — liga `is-animating` no ícone (hover por JS, como o card).
      linhaEmHover: null,
    }
  },
  computed: {
    linhas() {
      return linhasDoIndice(this.artefatos, this.identidadeAtiva)
    },
    total() {
      return this.linhas.length
    },
    temLinhas() {
      return this.total > 0
    },
    // Preferência de régua prevalece; cabe → menu; não cabe → régua, ou o
    // menu SOBREPOSTO se maximizado. Muda a cada medida.
    visaoEfetiva() {
      if (this.prefereRegua) return VISOES_DO_INDICE.compacta
      if (this.cabeMenu || this.maximizado) return this.visao
      return VISOES_DO_INDICE.compacta
    },
    compacta() {
      return this.visaoEfetiva === VISOES_DO_INDICE.compacta
    },
    condensada() {
      return this.visaoEfetiva === VISOES_DO_INDICE.condensada
    },
    completa() {
      return this.visaoEfetiva === VISOES_DO_INDICE.completa
    },
    // Completa e sobreposta: largura fixa. Condensada sem medida segue o
    // conteúdo (piso/teto do `<style>`); com faixa, largura FIXA nela
    // (`min-width` inline também: o do `<style>` é 180 e venceria um `width` menor).
    // O cabeçalho carrega o respiro do cartão (o `pa-2` saiu): é ele que põe
    // o botão no MESMO x/y do chevron da régua (`RESPIRO_DO_TOPO`).
    estiloDoTopo() {
      const r = RESPIRO_DO_TOPO
      const lados = [r.cima, r.direita, r.baixo, r.esquerda]
      return { padding: lados.map((lado) => lado + 'px').join(' ') }
    },
    // Teto de `MAX_LINHAS_VISIVEIS_NO_MENU` (7) linhas; o resto rola por dentro.
    estiloDaLista() {
      return { maxHeight: alturaMaximaDaListaDoMenu() + 'px' }
    },
    estiloDoCartao() {
      if (this.completa) return { width: LARGURA_COMPLETA + 'px' }
      if (this.sobreposto) return { width: LARGURA_MAXIMA_DO_CARTAO + 'px' }
      if (this.larguraMaxima === null) return null
      const largura = larguraDoCartaoCondensado(this.larguraMaxima) + 'px'
      return { width: largura, minWidth: largura }
    },
    // Leitura direta do store, sem `mapGetters` (como o
    // `artefato-preview-card.vue`: os testes montam com um `$store` plano).
    identidadeAtiva() {
      if (!this.$store) return null
      const chave = this.storeModule + '/artefatoIdentidadeAtiva'
      return this.$store.getters[chave] || null
    },
  },
  methods: {
    // Chevron da régua: menu na faixa se cabe, sobreposto se não; apaga a
    // preferência de régua.
    expandir() {
      this.prefereRegua = false
      gravarPrefereRegua(false)
      if (!this.cabeMenu) this.maximizar()
    },
    // Do menu (normal ou sobreposto) para a régua, gravando a preferência.
    minimizar() {
      this.prefereRegua = true
      this.recolher()
      gravarPrefereRegua(true)
    },
    // Completa ↔ condensada. Sem botão na UI desde 14/09/2026 (ver o cabeçalho).
    alternarDensidade() {
      this.visao = this.condensada
        ? VISOES_DO_INDICE.completa
        : VISOES_DO_INDICE.condensada
    },
    classesDoIconeCondensado(linha) {
      return {
        'is-detailed': linha.iconeDetalhado,
        'is-animating': this.linhaEmHover === linha.identidade,
      }
    },
    // Linha NÃO ativa: abre. O cartão continua aberto.
    abrir(linha) {
      this.$emit('abrir', {
        identidade: linha.identidade,
        parteReal: linha.parteReal,
      })
    },
    // Linha JÁ ativa: fecha o aside — toggle, como no thread.
    fecharArtefato() {
      this.$emit('fechar')
    },
    // Na condensada não há card mini para decidir o toggle — a linha decide.
    escolherCondensada(linha) {
      if (linha.ativa) {
        this.fecharArtefato()
        return
      }
      this.abrir(linha)
    },
  },
}
</script>

<template>
  <div v-if="temLinhas" class="chat-indice-artefatos">
    <!-- Sem `mode`: régua e menu coexistem durante a troca (quem sai vira
         absoluto no canto) — nunca há um quadro sem nada no canto. -->
    <transition name="iv2-indice-artefatos">
      <!-- A régua (estado mínimo): sem cabeçalho de texto; o expandir e o
           toggle saem dela. Mesma casca visual do cartão (classe). -->
      <ChatIndiceArtefatosCompacta
        v-if="compacta"
        key="compacta"
        class="chat-indice-artefatos__cartao"
        :linhas="linhas"
        @abrir="abrir"
        @fechar="fecharArtefato"
        @expandir="expandir"
      />

      <!-- <nav> (Sonar S6819 reprova `role="group"` em div); sem <aside> acima. -->
      <nav
        v-else
        key="cartao"
        class="chat-indice-artefatos__cartao px-2 pb-2"
        :class="{
          'chat-indice-artefatos__cartao--condensado': condensada,
          'chat-indice-artefatos__cartao--sobreposto': sobreposto,
        }"
        :style="estiloDoCartao"
        :aria-label="titulo"
        data-test="chat-indice-artefatos-cartao"
      >
        <div
          class="chat-indice-artefatos__topo ia-texto-corpo d-flex align-center justify-space-between"
          :style="estiloDoTopo"
          data-test="chat-indice-artefatos-topo"
        >
          <span
            class="d-inline-flex align-center az-gap-xsmall text-truncate"
            :title="titulo"
          >
            <span class="text-truncate">{{ tituloCurto }}</span>
            <span
              class="chat-indice-artefatos__contagem"
              data-test="chat-indice-artefatos-contagem"
              >{{ total }}</span
            >
          </span>
          <!-- UM botão só: a flecha leva à régua e grava a preferência (o
               "—" de minimizar saiu em 14/09/2026 — os dois faziam a mesma
               coisa aos olhos do usuário). Vale no menu normal e no sobreposto.
               O botão de densidade saiu no mesmo dia — ver o cabeçalho. -->
          <button
            type="button"
            class="chat-indice-artefatos__acao d-inline-flex align-center justify-center flex-shrink-0"
            :aria-label="rotuloMinimizar"
            :title="rotuloMinimizar"
            data-test="chat-indice-artefatos-minimizar"
            @click="minimizar"
          >
            <icon-svg
              :name="ICONE_DE_RECOLHER"
              :size="TAMANHO_DO_ICONE_DO_TOPO"
              color="var(--neutral-600)"
            />
          </button>
        </div>

        <!-- Condensada: ícone do módulo + título, um <button> por linha. -->
        <ul
          v-if="condensada"
          class="chat-indice-artefatos__lista"
          :style="estiloDaLista"
          data-test="chat-indice-artefatos-lista"
        >
          <li v-for="linha in linhas" :key="linha.identidade">
            <button
              type="button"
              class="chat-indice-artefatos__linha ia-texto-corpo d-flex align-center az-gap-small text-left px-2 py-1"
              :class="{ 'chat-indice-artefatos__linha--ativa': linha.ativa }"
              :title="linha.titulo"
              :aria-current="linha.ativa ? 'true' : null"
              data-test="chat-indice-artefatos-linha-condensada"
              @click="escolherCondensada(linha)"
              @mouseenter="linhaEmHover = linha.identidade"
              @mouseleave="linhaEmHover = null"
            >
              <icon-svg
                class="navbar-main-icon flex-shrink-0"
                :class="classesDoIconeCondensado(linha)"
                :name="linha.icone"
                :size="TAMANHO_DO_ICONE_DA_LINHA"
                color="primary"
              />
              <span class="text-truncate">{{ linha.titulo }}</span>
            </button>
          </li>
        </ul>

        <!-- Completa (viva, fora da UI desde 14/09/2026 — ver o cabeçalho):
             um card mini por artefato, no irmão. A casca da lista é a MESMA
             classe (o scoped alcança o root dele); o toggle sai do card. -->
        <ChatIndiceArtefatosCompleta
          v-else-if="completa"
          class="chat-indice-artefatos__lista"
          :linhas="linhas"
          :store-module="storeModule"
          @abrir="abrir"
          @fechar="fecharArtefato"
        />
      </nav>
    </transition>
  </div>
</template>

<style lang="sass" scoped>
// O mapa de animações POR ÍCONE do menu lateral — o MESMO partial que o
// `artefato-preview-card.vue` e o `nav-item.vue` importam (assume
// `<icon-svg class="navbar-main-icon">` + `.is-detailed`/`.is-animating`;
// keyframes `az-*` globais em `src/design/helpers/_effects.sass`). Só a
// linha condensada precisa dele aqui — na completa (irmão) quem anima é o card.
@import '@modules/navegacao-v2/styles/_nav-icon-tells.sass'

// ── Troca régua ↔ menu ───────────────────────────────────────────────────
// Sem `mode`: os dois nós coexistem durante a troca. Quem SAI é tirado do
// fluxo (absoluto no canto superior direito da casca, que já é absoluta no
// `messages.vue`) e some em fade; quem ENTRA chega em fade + leve escala a
// partir do MESMO canto. 200ms (`--motion-duration-default`), easing padrão.
// Duas classes de propósito: um nó com `transition` própria de uma classe
// só (o caso da visão mínima anterior, removida em 14/09/2026) venceria esta e o
// `opacity` não animaria (foi isso que deixou ~1s de vazio com o `out-in`).
.chat-indice-artefatos
  .iv2-indice-artefatos-enter-active,
  .iv2-indice-artefatos-leave-active
    transition: opacity var(--motion-duration-default) var(--motion-easing-standard), transform var(--motion-duration-default) var(--motion-easing-standard)
    transform-origin: top right

  .iv2-indice-artefatos-leave-active
    position: absolute
    top: 0
    right: 0
    pointer-events: none

  .iv2-indice-artefatos-enter,
  .iv2-indice-artefatos-leave-to
    opacity: 0
    transform: scale(0.96)

@media (prefers-reduced-motion: reduce)
  .chat-indice-artefatos
    .iv2-indice-artefatos-enter-active,
    .iv2-indice-artefatos-leave-active
      transition: none

    .iv2-indice-artefatos-enter,
    .iv2-indice-artefatos-leave-to
      transform: none

// O cartão: hairline `--border-hairline` + `--shadow-sm` sobre `--white`
// (print do usuário, 14/09/2026 — o "sem borda" foi tentado e revertido no
// mesmo dia: branco sobre o trilho branco sumia) e o raio. O padding é
// utility (`px-2 pb-2`; o de cima é do cabeçalho — `estiloDoTopo`, para o
// botão cair no mesmo x/y do chevron da régua). A completa tem largura fixa
// pelo template; a condensada se ajusta ao conteúdo entre o piso e o teto.
.chat-indice-artefatos__cartao
  box-sizing: border-box
  border: 1px solid var(--border-hairline)
  border-radius: var(--radius-lg)
  background: var(--white)
  box-shadow: var(--shadow-sm)
  overflow: hidden

// Só valem SEM medida (`larguraMaxima` nulo). Com faixa, `estiloDoCartao`
// fixa `width`/`min-width` inline em `clamp(128, faixa, 240)` — o teto é o
// mesmo `LARGURA_MAXIMA_DO_CARTAO` (teste trava o par).
$largura-minima-condensada: 180px
$largura-maxima-condensada: 240px

.chat-indice-artefatos__cartao--condensado
  min-width: $largura-minima-condensada
  max-width: $largura-maxima-condensada

// Maximizado por cima do chat (sem faixa; chevron da régua): a sombra de
// popover diz que é uma camada. A casca do `messages.vue` (`z-index: 2`) já
// o empilha acima das bolhas.
.chat-indice-artefatos__cartao--sobreposto
  box-shadow: var(--shadow-pop)

// Padding do cabeçalho é inline (`estiloDoTopo`, das constantes) — aqui só
// a tipografia. O cabeçalho sai do `px-2` do cartão para o botão ficar a
// `RESPIRO_DO_TOPO.direita` da borda.
.chat-indice-artefatos__topo
  color: var(--neutral-800)
  margin: 0 -8px

  // O bloco do título precisa poder encolher para o `text-truncate` agir.
  > span:first-child
    min-width: 0

// `--neutral-600`: a folha não declara `--neutral-500` (var inválida herdaria o 800 do topo).
.chat-indice-artefatos__contagem
  color: var(--neutral-600)
  font-weight: 400

.chat-indice-artefatos__acao
  width: 20px
  height: 20px
  border: 0
  border-radius: var(--radius-sm)
  background: transparent
  cursor: pointer

  &:hover,
  &:focus-visible
    background: var(--sidebar-warm-hover)
    outline: 0

// A lista rola por dentro: na condensada o teto é inline (7 linhas,
// `MAX_LINHAS_VISIVEIS_NO_MENU`); na completa ficam os 320px. Scrollbar FINA
// (receita da lateral, `chat-sidebar.vue::__corpo`: trilho transparente,
// polegar quente, 4px) e `scrollbar-gutter: stable` para as linhas não
// pularem quando a barra entra.
$largura-da-scrollbar: 4px

.chat-indice-artefatos__lista
  list-style: none
  margin: 0
  padding: 0
  max-height: 320px
  overflow-y: auto
  scrollbar-gutter: stable
  // ⚠️ SEM as propriedades padrão de barra (width/color): com elas o Chrome
  // ignora o `::-webkit-scrollbar` e desenha a barra nativa grossa (14/09/2026).
  &::-webkit-scrollbar
    width: $largura-da-scrollbar

  &::-webkit-scrollbar-track
    background: transparent

  &::-webkit-scrollbar-thumb
    background: var(--sidebar-warm-border)
    box-shadow: none

    &:hover
      background: var(--neutral-300)

// A linha condensada: ícone + título. Hover das NÃO ativas no par quente do
// índice de mensagens; a ATIVA copia SÓ o wash do estado `--ativo` do
// `artefato-preview-card` — o MESMO 3% de `primary` via `color-mix`, mantido
// no hover —, SEM a borda `primary` que o card tem (pedido do usuário,
// 14/09/2026: numa linha de 26px o contorno pesava; a linha não tem borda).
// Padding é utility (`px-2 py-1`). O ícone anima pelo partial importado acima.
.chat-indice-artefatos__linha
  color: var(--neutral-900)
  width: 100%
  border: 0
  border-radius: var(--radius-sm)
  background: transparent
  cursor: pointer
  transition: background var(--motion-duration-fast) var(--motion-easing-standard)

  &:hover
    background: var(--sidebar-warm-hover)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: -2px

  &[aria-current="true"]
    background: color-mix(in srgb, var(--primary) 3%, var(--white))
    color: var(--neutral-900)
    font-weight: 600

    &:hover
      background: color-mix(in srgb, var(--primary) 3%, var(--white))

  > .text-truncate
    min-width: 0
</style>
